'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Activity, Database, ExternalLink, Radio, Server } from 'lucide-react';
import { ClientDate } from '../ClientDate';
import type { IntegrationService, PlatformHealth, RecentActivity } from '../types';

export function SystemPanel({
  health,
  activity,
  integrations,
  loading,
  onRefresh,
}: {
  health: PlatformHealth | null;
  activity: RecentActivity | null;
  integrations: IntegrationService[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const statusItems = useMemo(() => {
    if (!health) return [];
    return [
      {
        label: 'PostgreSQL',
        ok: health.database.ok,
        detail: `${health.database.latency_ms}ms`,
        icon: <Database className="w-4 h-4" />,
      },
      {
        label: 'AI Voice',
        ok: health.voice.ok,
        detail: health.voice.ok ? `${health.voice.latency_ms}ms` : health.voice.endpoint,
        icon: <Radio className="w-4 h-4" />,
      },
      {
        label: 'Ambiente',
        ok: true,
        detail: health.environment.node_env,
        icon: <Server className="w-4 h-4" />,
      },
    ];
  }, [health]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  if (loading) {
    return <p className="text-sm text-ink-500">A carregar estado do sistema...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="sa-toolbar">
        <p className="text-sm text-ink-500">
          Saude dos servicos e atividade recente da plataforma.
        </p>
        <button type="button" onClick={handleRefresh} disabled={refreshing} className="btn btn-secondary btn-sm">
          Rever servicos
        </button>
      </div>

      <div className="sa-panel-grid sa-panel-grid-3">
        {statusItems.map((item) => (
          <div key={item.label} className="sa-kpi">
            <div className="flex items-center gap-2 text-ink-500">
              {item.icon}
              <p className="sa-kpi-label !normal-case !tracking-normal">{item.label}</p>
            </div>
            <p className="sa-kpi-value text-xl">
              <span className={`sa-status sa-status-${item.ok ? 'ok' : 'danger'}`}>
                {item.ok ? 'OK' : 'Falha'}
              </span>
            </p>
            <p className="sa-kpi-sub truncate">{item.detail}</p>
          </div>
        ))}
      </div>

      {health && (
        <p className="text-xs text-ink-500">
          Ultima verificacao: <ClientDate value={health.checked_at} />
        </p>
      )}

      {integrations.length > 0 && (
        <section className="sa-card">
          <header className="sa-card-header">
            <h3 className="sa-card-title">Integracoes</h3>
            <p className="sa-card-desc">PBX Manager, AI Voice e servicos externos</p>
          </header>
          <div className="space-y-2">
            {integrations.map((s) => (
              <div key={s.name} className="sa-list-row">
                <span className="font-medium">{s.name}</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`sa-status sa-status-${s.ok ? 'ok' : 'danger'}`}>{s.ok ? 'OK' : 'Falha'}</span>
                  <span className="text-xs text-ink-500 truncate max-w-[240px]">{s.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="sa-two-col">
        <section className="sa-card">
          <header className="sa-card-header">
            <h3 className="sa-card-title flex items-center gap-2">
              <Activity className="w-4 h-4" /> Pedidos recentes
            </h3>
            <p className="sa-card-desc">Ultimos 25 pedidos em todos os tenants</p>
          </header>
          <div className="sa-mini-table">
            {(activity?.orders ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">Sem pedidos registados.</p>
            ) : (
              activity?.orders.map((o) => (
                <div key={o.id} className="sa-mini-row">
                  <div className="min-w-0">
                    <p className="font-medium text-ink-900 truncate">
                      #{o.order_number} · {o.tenant_name}
                    </p>
                    <p className="text-xs text-ink-500">
                      {o.status} · {o.channel} · <ClientDate value={o.created_at} />
                    </p>
                  </div>
                  <span className="sa-pill">€{Number(o.total).toFixed(2)}</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="sa-card">
          <header className="sa-card-header">
            <h3 className="sa-card-title">Logins super-admin</h3>
            <p className="sa-card-desc">Ultimos acessos ao painel de plataforma</p>
          </header>
          <div className="sa-mini-table">
            {(activity?.logins ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">Sem logins registados.</p>
            ) : (
              activity?.logins.map((l) => (
                <div key={l.email} className="sa-mini-row">
                  <div className="min-w-0">
                    <p className="font-medium text-ink-900 truncate">{l.name}</p>
                    <p className="text-xs text-ink-500 font-mono truncate">{l.email}</p>
                  </div>
                  <ClientDate value={l.last_login_at} />
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section className="sa-card">
        <header className="sa-card-header">
          <h3 className="sa-card-title">Atalhos operacionais</h3>
        </header>
        <div className="flex flex-wrap gap-2">
          <a href="https://okai.pt" target="_blank" rel="noreferrer" className="sa-chip">
            <ExternalLink className="w-3.5 h-3.5" /> Landing okai.pt
          </a>
          <Link href="/super-admin/tenants" className="sa-chip">
            Gerir tenants
          </Link>
          <Link href="/super-admin/voz" className="sa-chip">
            Inventario de voz
          </Link>
        </div>
      </section>
    </div>
  );
}
